import { useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { LogOut, ChevronDown } from 'lucide-react';

import { useAuth } from '../auth/AuthProvider.jsx';
import { env } from '../lib/env.js';
import { ADMIN_NAV } from './adminNav.js';

function flattenItems(nav) {
  const out = [];
  for (const group of nav) {
    if (group.section) {
      for (const it of group.items || []) out.push(it);
    } else {
      out.push(group);
    }
  }
  return out;
}

function findActive(items, pathname) {
  // Pick the longest match so /admin/ai/personality wins over /admin/ai.
  let match = null;
  for (const it of items) {
    if (it.to && (pathname === it.to || pathname.startsWith(`${it.to}/`))) {
      if (!match || it.to.length > match.to.length) match = it;
    }
  }
  return match;
}

function NavItem({ item, hasPermission }) {
  if (item.perm && !hasPermission(item.perm)) return null;
  const Icon = item.icon;
  return (
    <li>
      <NavLink
        to={item.to}
        className={({ isActive }) =>
          clsx(
            'group flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors',
            isActive
              ? 'bg-sidebar-active text-sidebar-ink font-medium'
              : 'text-sidebar-ink-muted hover:bg-sidebar-elevated hover:text-sidebar-ink'
          )
        }
      >
        {Icon && (
          <Icon
            className="h-4 w-4 shrink-0 text-sidebar-ink-muted group-hover:text-sidebar-ink"
            strokeWidth={1.75}
          />
        )}
        <span>{item.label}</span>
      </NavLink>
    </li>
  );
}

function UserMenu({ user, role, onLogout }) {
  const [open, setOpen] = useState(false);
  const initials = (user && user.email ? user.email[0] : '?').toUpperCase();
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left hover:bg-sidebar-elevated"
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-ink text-[11px] font-semibold text-white">
          {initials}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-medium text-sidebar-ink">{user && user.email}</p>
          <p className="text-[11px] text-sidebar-ink-muted">{role}</p>
        </div>
        <ChevronDown className={clsx('h-3.5 w-3.5 text-sidebar-ink-muted transition-transform', open && 'rotate-180')} strokeWidth={1.75} />
      </button>
      {open && (
        <div className="animate-zoom-in absolute bottom-full left-0 right-0 mb-1 origin-bottom rounded-md border border-border bg-surface-elevated p-1 shadow-elevated">
          <NavLink
            to="/admin/account"
            onClick={() => setOpen(false)}
            className="block rounded px-2 py-1.5 text-xs text-ink hover:bg-surface-subtle"
          >
            Account e sicurezza
          </NavLink>
          <button
            type="button"
            onClick={onLogout}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs text-ink hover:bg-surface-subtle"
          >
            <LogOut className="h-3.5 w-3.5" strokeWidth={1.75} />
            Esci
          </button>
        </div>
      )}
    </div>
  );
}

export function AdminLayout() {
  const { user, role, hasPermission, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const onLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const flat = useMemo(() => flattenItems(ADMIN_NAV), []);
  const active = useMemo(() => findActive(flat, location.pathname), [flat, location.pathname]);

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-surface">
      <aside className="flex w-64 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-ink">
        <div className="flex items-center gap-2 px-4 py-4">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-white border border-sidebar-border text-white">
            <img src="/favicon.png" alt={env.appName} className="h-4 w-4" />
          </span>
          <p className="text-sm font-semibold tracking-tight text-sidebar-ink">{env.appName}</p>
        </div>
        <div className="border-t border-sidebar-border" />
        <nav className="flex-1 overflow-y-auto px-2 py-3">
          {ADMIN_NAV.map((group, idx) => {
            const visible = (group.items || []).filter((it) => !it.perm || hasPermission(it.perm));
            if (visible.length === 0) return null;
            return (
              <div key={idx} className="mb-4">
                <p className="px-2.5 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-sidebar-ink-muted">
                  {group.section}
                </p>
                <ul className="flex flex-col gap-0.5">
                  {visible.map((it) => (
                    <NavItem key={it.to} item={it} hasPermission={hasPermission} />
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>
        <div className="border-t border-sidebar-border p-2">
          <UserMenu user={user} role={role} onLogout={onLogout} />
        </div>
      </aside>

      <main className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-12 items-center justify-between border-b border-border bg-surface-elevated px-6">
          <div className="flex items-center gap-2 text-xs text-ink-muted">
            <span>Admin</span>
            {active && (
              <>
                <span aria-hidden="true">/</span>
                <span className="text-ink">{active.label}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-ink-muted">
            <span className="hidden sm:inline">{user && user.email}</span>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto">
          <div key={location.pathname} className="animate-fade-in mx-auto max-w-7xl px-8 py-8">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
}
